import { useCallback, useEffect, useMemo, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { GhostButton, ActionButton, PillBadge } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { toast } from "sonner";
import {
  BellRing,
  Clock,
  Users,
  Plus,
  Trash2,
  Edit3,
  ShieldCheck,
  Zap,
  Volume2,
  VolumeX,
  Send,
  CheckCircle2,
  XCircle,
  Mail,
} from "lucide-react";

function isEmail(v) {
  return typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function Toggle({ enabled, onChange, testId, size = "md" }) {
  const dims = size === "sm" ? "h-5 w-9" : "h-6 w-11";
  const dot = size === "sm" ? "size-3.5" : "size-4";
  const on = size === "sm" ? "translate-x-5" : "translate-x-6";
  return (
    <button
      onClick={() => onChange(!enabled)}
      data-testid={testId}
      className={`relative inline-flex ${dims} items-center transition-colors border ${
        enabled ? "bg-[#FF3B30] border-[#FF3B30]" : "bg-[#141414] border-[#27272A]"
      }`}
      aria-checked={enabled}
      role="switch"
    >
      <span className={`inline-block ${dot} transform bg-white transition-transform ${enabled ? on : "translate-x-1"}`} />
    </button>
  );
}

// ─── Master Switch + Quiet Hours ────────────────────────────────────────────
function MasterSwitchCard({ cfg, onChange }) {
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="master-switch-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <ShieldCheck className="size-4 text-zinc-400" />
        <div className="label-mute">Master switch</div>
      </div>
      <div className="px-5 py-4 flex items-center justify-between">
        <div>
          <div className="text-sm text-zinc-200 font-medium">
            {cfg.master_enabled ? "All operational notifications active" : "Operational notifications paused"}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Turning this off pauses every operational email. Transactional flows (password reset,
            invites, welcome emails) always fire regardless.
          </div>
        </div>
        <Toggle
          enabled={!!cfg.master_enabled}
          onChange={(v) => onChange({ master_enabled: v })}
          testId="master-enabled-toggle"
        />
      </div>
    </div>
  );
}

function QuietHoursCard({ cfg, onChange }) {
  const q = cfg.quiet_hours || {};
  const setField = (patch) => onChange({ quiet_hours: { ...q, ...patch } });
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="quiet-hours-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        {q.enabled ? <VolumeX className="size-4 text-[#FF3B30]" /> : <Volume2 className="size-4 text-zinc-400" />}
        <div className="label-mute">Quiet hours</div>
        <div className="ml-auto">
          <Toggle
            enabled={!!q.enabled}
            onChange={(v) => setField({ enabled: v })}
            testId="quiet-hours-toggle"
            size="sm"
          />
        </div>
      </div>
      <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
        Non-critical emails (digests, DAR updates, metrics snapshot) will be paused between the two
        hours below. <span className="text-zinc-300">Critical alerts</span> (tardy, incidents, geofence,
        complaints, status-check misses, dispatch) always send.
      </div>
      <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="label-mute mb-1">Start hour (UTC)</div>
          <select
            value={q.start_hour_utc ?? 2}
            onChange={(e) => setField({ start_hour_utc: Number(e.target.value) })}
            disabled={!q.enabled}
            data-testid="quiet-hours-start"
            className="w-full bg-transparent border border-[#27272A] px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30] disabled:opacity-40"
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h} className="bg-[#0A0A0A]">{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label-mute mb-1">End hour (UTC)</div>
          <select
            value={q.end_hour_utc ?? 10}
            onChange={(e) => setField({ end_hour_utc: Number(e.target.value) })}
            disabled={!q.enabled}
            data-testid="quiet-hours-end"
            className="w-full bg-transparent border border-[#27272A] px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30] disabled:opacity-40"
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h} className="bg-[#0A0A0A]">{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── Rosters ────────────────────────────────────────────────────────────────
function RostersCard({ rosters, onCreate, onUpdate, onDelete }) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", emails: [""] });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const startCreate = () => {
    setCreating(true);
    setDraft({ name: "", emails: [""] });
  };
  const cancelCreate = () => setCreating(false);

  const submitCreate = async () => {
    const emails = draft.emails.map((e) => e.trim()).filter(Boolean);
    if (!draft.name.trim() || !emails.every(isEmail)) {
      toast.error("Provide a name and valid emails");
      return;
    }
    await onCreate({ name: draft.name.trim(), emails });
    setCreating(false);
  };
  const startEdit = (r) => {
    setEditingId(r.id);
    setEditDraft({ name: r.name, emails: [...r.emails, ""] });
  };
  const submitEdit = async () => {
    const emails = editDraft.emails.map((e) => e.trim()).filter(Boolean);
    if (!editDraft.name.trim() || !emails.every(isEmail)) {
      toast.error("Provide a name and valid emails");
      return;
    }
    await onUpdate(editingId, { name: editDraft.name.trim(), emails });
    setEditingId(null);
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="rosters-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <Users className="size-4 text-zinc-400" />
        <div className="label-mute">Recipient rosters</div>
        <div className="ml-auto">
          <GhostButton onClick={startCreate} disabled={creating} data-testid="roster-add">
            <Plus className="size-3.5 mr-1" /> New roster
          </GhostButton>
        </div>
      </div>
      <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
        Reusable named lists (e.g. <span className="text-zinc-300">Leadership</span>,
        {" "}<span className="text-zinc-300">Ops Team</span>). Any stream below can subscribe to a roster —
        change the roster once and every stream picks up the new emails.
      </div>
      <div className="divide-y divide-[#1c1c1e]">
        {creating && (
          <div className="px-5 py-4 space-y-3 bg-[#141414]" data-testid="roster-create-form">
            <input
              type="text"
              placeholder="Roster name (e.g. Leadership)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              data-testid="roster-name-new"
              className="w-full bg-transparent border border-[#27272A] px-2 py-1.5 text-sm text-zinc-200 outline-none focus:border-[#FF3B30]"
            />
            {draft.emails.map((e, i) => (
              <input
                key={i}
                type="email"
                placeholder="email@company.com"
                value={e}
                onChange={(ev) => {
                  const arr = [...draft.emails];
                  arr[i] = ev.target.value;
                  if (i === arr.length - 1 && ev.target.value) arr.push("");
                  setDraft({ ...draft, emails: arr });
                }}
                data-testid={`roster-email-new-${i}`}
                className={`w-full bg-transparent border px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30] ${
                  e.trim() && !isEmail(e) ? "border-[#FF3B30]" : "border-[#27272A]"
                }`}
              />
            ))}
            <div className="flex gap-2">
              <ActionButton onClick={submitCreate} data-testid="roster-save-new">Create</ActionButton>
              <GhostButton onClick={cancelCreate}>Cancel</GhostButton>
            </div>
          </div>
        )}
        {rosters.length === 0 && !creating && (
          <div className="px-5 py-6 text-xs text-zinc-600 italic" data-testid="rosters-empty">
            No rosters yet — add one so streams can subscribe.
          </div>
        )}
        {rosters.map((r) => {
          const isEditing = editingId === r.id;
          return (
            <div key={r.id} className="px-5 py-4" data-testid={`roster-row-${r.id}`}>
              {isEditing ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editDraft.name}
                    onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                    data-testid={`roster-name-edit-${r.id}`}
                    className="w-full bg-transparent border border-[#27272A] px-2 py-1.5 text-sm text-zinc-200 outline-none focus:border-[#FF3B30]"
                  />
                  {editDraft.emails.map((e, i) => (
                    <input
                      key={i}
                      type="email"
                      value={e}
                      onChange={(ev) => {
                        const arr = [...editDraft.emails];
                        arr[i] = ev.target.value;
                        if (i === arr.length - 1 && ev.target.value) arr.push("");
                        setEditDraft({ ...editDraft, emails: arr });
                      }}
                      className={`w-full bg-transparent border px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30] ${
                        e.trim() && !isEmail(e) ? "border-[#FF3B30]" : "border-[#27272A]"
                      }`}
                    />
                  ))}
                  <div className="flex gap-2">
                    <ActionButton onClick={submitEdit} data-testid={`roster-save-edit-${r.id}`}>Save</ActionButton>
                    <GhostButton onClick={() => setEditingId(null)}>Cancel</GhostButton>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 font-medium">{r.name}</div>
                    <div className="text-[11px] text-zinc-500 mt-1 font-mono truncate">
                      {r.emails.join(", ") || <em>(no emails)</em>}
                    </div>
                  </div>
                  <button
                    onClick={() => startEdit(r)}
                    data-testid={`roster-edit-${r.id}`}
                    className="text-zinc-500 hover:text-zinc-200 p-1"
                    aria-label="Edit roster"
                  >
                    <Edit3 className="size-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(r.id)}
                    data-testid={`roster-delete-${r.id}`}
                    className="text-zinc-500 hover:text-[#FF3B30] p-1"
                    aria-label="Delete roster"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Subscriber groups picker (iter246) ────────────────────────────────────
// Combined searchable multi-select of derived groups (roles / clients /
// sites / users). Renders one MultiSelectDropdown per bucket so the caller
// can find + tick options fast without a giant flat list.
function SubscriberGroupsPicker({ streamKey, options, selected, onChange }) {
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);

  // Reflect any option changes back to the parent as a flat array. Deduplicated
  // per bucket by key prefix so replacing selections in one dropdown doesn't
  // clobber picks from another bucket.
  const setBucket = useCallback((prefix, nextValues) => {
    // Drop existing prefix matches, add the new values (deduped).
    const other = (selected || []).filter((x) => !x.startsWith(prefix));
    const merged = Array.from(new Set([...other, ...nextValues]));
    onChange(merged);
  }, [selected, onChange]);

  if (!options) {
    return (
      <div>
        <div className="text-sm text-zinc-200 font-medium mb-2">Subscriber groups</div>
        <div className="text-[11px] text-zinc-500 italic">Loading groups…</div>
      </div>
    );
  }

  const buckets = [
    { key: "role",         prefix: "role:",         title: "By role",         opts: options.roles         || [] },
    { key: "custom_role",  prefix: "custom_role:",  title: "By custom role",  opts: options.custom_roles  || [] },
    { key: "client",       prefix: "client:",       title: "By client",       opts: options.clients       || [] },
    { key: "site",         prefix: "site:",         title: "By site",         opts: options.sites         || [] },
    { key: "user",         prefix: "user:",         title: "Individual users", opts: options.users         || [] },
  ];

  const totalSelected = (selected || []).length;

  return (
    <div data-testid={`stream-${streamKey}-subscriber-groups`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm text-zinc-200 font-medium">Subscriber groups</div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          {totalSelected} selected
        </span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
        Auto-resolve to the current members every time this stream fires — pick a role, a client, a
        site, or specific users. Deleted users silently drop out.
      </p>
      <div className="space-y-2">
        {buckets.map(({ key, prefix, title, opts }) => {
          const bucketSelected = (selected || []).filter((x) => x.startsWith(prefix));
          const dropdownOptions = opts.map((o) => ({
            value: o.id,
            label: o.label || o.id,
            sub: o.sub || (typeof o.count === "number" ? `${o.count} member${o.count === 1 ? "" : "s"}` : undefined),
          }));
          if (dropdownOptions.length === 0) return null;
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 w-32 shrink-0">
                {title}
              </div>
              <div className="flex-1">
                <MultiSelectDropdown
                  testId={`stream-${streamKey}-subs-${key}`}
                  label={`Pick ${title.toLowerCase().replace("by ", "").replace("individual ", "")}`}
                  options={dropdownOptions}
                  selected={bucketSelected}
                  onChange={(next) => setBucket(prefix, next)}
                  searchable
                  triggerClassName="w-full"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Stream Drawer ──────────────────────────────────────────────────────────
function StreamDrawer({ stream, config, rosters, subscriberOptions, onSave, onClose }) {
  const [draft, setDraft] = useState(() => ({
    enabled: config.enabled,
    recipients: (config.recipients && config.recipients.length > 0) ? [...config.recipients, ""] : [""],
    roster_ids: [...(config.roster_ids || [])],
    subscriber_ids: [...(config.subscriber_ids || [])],
    thresholds: { ...(config.thresholds || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [feed, setFeed] = useState({ loading: true, rows: [] });
  const [testing, setTesting] = useState(false);

  const invalid = draft.recipients.some((e) => e.trim() && !isEmail(e));
  const dirty = useMemo(() => {
    const norm = (arr) => (arr || []).map((s) => s.trim().toLowerCase()).filter(Boolean).sort();
    return (
      draft.enabled !== config.enabled ||
      JSON.stringify(norm(draft.recipients)) !== JSON.stringify(norm(config.recipients)) ||
      JSON.stringify([...(draft.roster_ids || [])].sort()) !== JSON.stringify([...(config.roster_ids || [])].sort()) ||
      JSON.stringify([...(draft.subscriber_ids || [])].sort()) !== JSON.stringify([...(config.subscriber_ids || [])].sort()) ||
      JSON.stringify(draft.thresholds || {}) !== JSON.stringify(config.thresholds || {})
    );
  }, [draft, config]);

  const loadFeed = useCallback(async () => {
    setFeed((f) => ({ ...f, loading: true }));
    try {
      const r = await api.get(`/notifications/console/streams/${stream.key}/log`, { params: { limit: 10 } });
      setFeed({ loading: false, rows: r.data.rows || [] });
    } catch (err) {
      setFeed({ loading: false, rows: [] });
    }
  }, [stream.key]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(stream.key, {
        enabled: draft.enabled,
        recipients: draft.recipients.map((e) => e.trim().toLowerCase()).filter(Boolean),
        roster_ids: draft.roster_ids,
        subscriber_ids: draft.subscriber_ids,
        thresholds: draft.thresholds,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    // Button is disabled while dirty (see render below) so this guard is
    // primarily a defensive one for keyboard-driven activation.
    if (dirty) return;
    setTesting(true);
    try {
      const r = await api.post(`/notifications/console/streams/${stream.key}/send-test`);
      if (r.data?.sent) {
        toast.success(`Test sent to ${r.data.recipients} recipient${r.data.recipients === 1 ? "" : "s"}`);
        loadFeed();
      } else if (r.data?.skipped === "no_recipients") {
        toast.error("No recipients configured — add one and save first");
      } else {
        toast.error(`Test failed: ${r.data?.reason || r.data?.skipped || "unknown"}`);
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex" data-testid={`stream-drawer-${stream.key}`}>
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md h-full bg-[#0A0A0A] border-l border-[#27272A] overflow-y-auto">
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#27272A] px-5 py-4 flex items-center gap-3 z-10">
          <div className="flex-1">
            <div className="label-mute">{stream.category}</div>
            <div className="text-lg font-medium text-zinc-100">{stream.label}</div>
          </div>
          <Toggle
            enabled={draft.enabled}
            onChange={(v) => setDraft({ ...draft, enabled: v })}
            testId={`stream-${stream.key}-enabled`}
          />
        </div>

        <div className="p-5 space-y-5">
          {/* Critical badge */}
          {stream.critical && (
            <div className="flex items-center gap-2 text-[11px] text-[#FF9F0A]">
              <Zap className="size-3" /> Critical stream — bypasses quiet hours when enabled
            </div>
          )}

          {/* Rosters */}
          <div>
            <div className="text-sm text-zinc-200 font-medium mb-2">Subscribed rosters</div>
            {rosters.length === 0 ? (
              <div className="text-[11px] text-zinc-500 italic">
                No rosters defined — create one on the main page.
              </div>
            ) : (
              <div className="space-y-1.5">
                {rosters.map((r) => {
                  const on = draft.roster_ids.includes(r.id);
                  return (
                    <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`stream-${stream.key}-roster-${r.id}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          setDraft((d) => ({
                            ...d,
                            roster_ids: on ? d.roster_ids.filter((x) => x !== r.id) : [...d.roster_ids, r.id],
                          }));
                        }}
                        className="accent-[#FF3B30]"
                      />
                      <span className="text-zinc-200">{r.name}</span>
                      <span className="text-[10px] text-zinc-500 ml-auto font-mono">{r.emails.length} email{r.emails.length === 1 ? "" : "s"}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Derived subscriber groups (iter246) — roles / clients / sites / users */}
          <SubscriberGroupsPicker
            streamKey={stream.key}
            options={subscriberOptions}
            selected={draft.subscriber_ids}
            onChange={(next) => setDraft((d) => ({ ...d, subscriber_ids: next }))}
          />

          {/* Inline recipients */}
          <div>
            <div className="text-sm text-zinc-200 font-medium mb-2">Extra recipients (inline)</div>
            {draft.recipients.map((e, i) => {
              const bad = e.trim() && !isEmail(e);
              return (
                <input
                  key={i}
                  type="email"
                  placeholder="email@company.com"
                  value={e}
                  onChange={(ev) => {
                    const arr = [...draft.recipients];
                    arr[i] = ev.target.value;
                    if (i === arr.length - 1 && ev.target.value) arr.push("");
                    setDraft({ ...draft, recipients: arr });
                  }}
                  data-testid={`stream-${stream.key}-recipient-${i}`}
                  className={`w-full bg-transparent border px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30] mb-1.5 ${
                    bad ? "border-[#FF3B30]" : "border-[#27272A]"
                  }`}
                />
              );
            })}
          </div>

          {/* Thresholds */}
          {stream.threshold_keys.length > 0 && (
            <div>
              <div className="text-sm text-zinc-200 font-medium mb-2">Thresholds</div>
              <div className="space-y-2">
                {stream.threshold_keys.map((k) => (
                  <div key={k}>
                    <div className="label-mute mb-1">{k.replace(/_/g, " ")}</div>
                    <input
                      type="text"
                      value={draft.thresholds[k] ?? ""}
                      onChange={(e) => setDraft({ ...draft, thresholds: { ...draft.thresholds, [k]: e.target.value } })}
                      data-testid={`stream-${stream.key}-threshold-${k}`}
                      className="w-full bg-transparent border border-[#27272A] px-2 py-1.5 text-sm font-mono text-zinc-200 outline-none focus:border-[#FF3B30]"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Recent delivery feed */}
          <div data-testid={`stream-${stream.key}-feed-section`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-zinc-200 font-medium">Recent deliveries</div>
              <GhostButton
                onClick={sendTest}
                disabled={testing || dirty}
                data-testid={`stream-${stream.key}-send-test`}
                title={dirty ? "Save your changes before sending a test" : "Fire a sample email through this stream's pipeline"}
              >
                <Send className="size-3 mr-1" /> {testing ? "Sending…" : "Send test now"}
              </GhostButton>
            </div>
            {feed.loading ? (
              <div className="text-[11px] text-zinc-500 italic">Loading…</div>
            ) : feed.rows.length === 0 ? (
              <div
                className="text-[11px] text-zinc-500 italic"
                data-testid={`stream-${stream.key}-feed-empty`}
              >
                No sends recorded yet for this stream.
              </div>
            ) : (
              <div className="border border-[#27272A] divide-y divide-[#1c1c1e]">
                {feed.rows.map((row) => {
                  const isTest = (row.dedupe_type || "").startsWith("test:");
                  const ok = !!row.sent;
                  return (
                    <div
                      key={row.id}
                      data-testid={`stream-${stream.key}-feed-row-${row.id}`}
                      className="px-3 py-2 text-[11px] font-mono flex items-start gap-2"
                      title={row.reason ? `reason: ${row.reason}` : (row.email_id ? `email_id: ${row.email_id}` : "")}
                    >
                      {ok ? (
                        <CheckCircle2 className="size-3 shrink-0 mt-0.5 text-[#34C759]" />
                      ) : (
                        <XCircle className="size-3 shrink-0 mt-0.5 text-[#FF3B30]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-zinc-500 text-[10px]">
                            {row.sent_at ? new Date(row.sent_at).toLocaleString(undefined, {
                              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                            }) : "—"}
                          </span>
                          {isTest && (
                            <span className="text-[9px] uppercase tracking-widest text-[#FF9F0A]">test</span>
                          )}
                          {!ok && row.reason && (
                            <span className="text-[9px] uppercase tracking-widest text-[#FF3B30]">{row.reason}</span>
                          )}
                        </div>
                        <div className="text-zinc-300 truncate">{row.subject || "(no subject)"}</div>
                        {(row.recipients?.length ?? 0) > 0 && (
                          <div className="text-zinc-500 text-[10px] truncate">
                            → {row.recipients.slice(0, 3).join(", ")}
                            {row.recipients.length > 3 ? ` +${row.recipients.length - 3}` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-[#27272A] px-5 py-3 flex gap-2 justify-end">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <ActionButton onClick={save} disabled={saving || invalid} data-testid={`stream-${stream.key}-save`}>
            {saving ? "Saving…" : "Save"}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

// ─── Streams Card ───────────────────────────────────────────────────────────
function StreamsCard({ streams, config, rosters, subscriberOptions, onSaveStream }) {
  const [openKey, setOpenKey] = useState(null);
  const byCategory = useMemo(() => {
    const g = {};
    for (const s of streams) {
      (g[s.category] ||= []).push(s);
    }
    return g;
  }, [streams]);

  const openStream = openKey ? streams.find((s) => s.key === openKey) : null;

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="streams-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <BellRing className="size-4 text-zinc-400" />
        <div className="label-mute">Notification streams</div>
      </div>
      <div className="px-5 py-3 text-xs text-zinc-500 border-b border-[#1c1c1e]">
        Every automated email that leaves this agency. Click a row to configure recipients, rosters,
        thresholds and enabled state.
      </div>
      {Object.entries(byCategory).map(([cat, rows]) => (
        <div key={cat} className="border-b border-[#1c1c1e] last:border-b-0">
          <div className="px-5 py-2 text-[10px] uppercase tracking-widest text-zinc-500 bg-[#0f0f0f]">
            {cat}
          </div>
          <div className="divide-y divide-[#1c1c1e]">
            {rows.map((s) => {
              const cfg = config.streams?.[s.key] || { enabled: true, recipients: [], roster_ids: [] };
              const recipientCount = (cfg.recipients?.length || 0) + (cfg.roster_ids?.length || 0);
              return (
                <button
                  key={s.key}
                  onClick={() => setOpenKey(s.key)}
                  data-testid={`stream-row-${s.key}`}
                  className="w-full px-5 py-3 flex items-center gap-3 hover:bg-[#141414] text-left"
                >
                  <div className={`size-2 shrink-0 rounded-full ${cfg.enabled ? "bg-[#34C759]" : "bg-zinc-700"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100">{s.label}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                      {cfg.enabled ? (
                        recipientCount === 0
                          ? <span className="text-[#FF9F0A]">enabled · no recipients</span>
                          : <>enabled · {recipientCount} target{recipientCount === 1 ? "" : "s"}</>
                      ) : (
                        <span className="text-zinc-500">disabled</span>
                      )}
                    </div>
                  </div>
                  {s.critical && (
                    <PillBadge tone="amber" testid={`stream-${s.key}-critical-badge`}>
                      <Zap className="size-3 mr-0.5" /> critical
                    </PillBadge>
                  )}
                  <div className="text-zinc-500 text-xs">›</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {openStream && (
        <StreamDrawer
          stream={openStream}
          config={config.streams[openStream.key] || { enabled: true, recipients: [], roster_ids: [], subscriber_ids: [], thresholds: {} }}
          rosters={config.rosters || []}
          subscriberOptions={subscriberOptions}
          onSave={onSaveStream}
          onClose={() => setOpenKey(null)}
        />
      )}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
function EmailTemplateGalleryCard() {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const onSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/system/email-preview/send-all");
      setResult(r.data);
      const okCount = r.data.sent?.length || 0;
      const failCount = r.data.failed?.length || 0;
      if (failCount === 0) {
        toast.success(`Sent ${okCount} preview email${okCount === 1 ? "" : "s"} to ${r.data.recipient}`);
      } else {
        toast.error(`${okCount} sent, ${failCount} failed — check the log below`);
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="border border-[#27272A] bg-[#121212] p-5" data-testid="email-preview-gallery-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Mail className="size-5 text-[#0A84FF] mt-1" />
          <div>
            <div className="text-sm font-bold text-zinc-100 uppercase tracking-wider">
              Email template gallery
            </div>
            <div className="text-xs text-zinc-500 mt-1 max-w-xl">
              Send one sample of every transactional Iris email (password reset, invite,
              cert expiry × 3, IR / MR / DAR default + detailed / Tour report, weekly digest)
              to your inbox. Each mail includes an &ldquo;Open in browser&rdquo; link and inline photo
              attachments so you can QA the full template gallery in seconds.
            </div>
          </div>
        </div>
        <ActionButton
          onClick={onSend}
          disabled={sending}
          data-testid="email-preview-send-all-btn"
        >
          <Send className="size-3.5" /> {sending ? "Sending…" : "Send preview set"}
        </ActionButton>
      </div>
      {result && (
        <div className="mt-4 border-t border-[#27272A] pt-3" data-testid="email-preview-result">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            {result.total} sample{result.total === 1 ? "" : "s"} &middot; {result.sent?.length || 0} sent &middot;
            {" "}{result.failed?.length || 0} failed &middot; to <span className="text-zinc-300 font-mono">{result.recipient}</span>
          </div>
          <ul className="text-xs text-zinc-400 space-y-1 max-h-48 overflow-y-auto">
            {(result.sent || []).map((s) => (
              <li key={s} className="flex items-start gap-2" data-testid="email-preview-sent-row">
                <CheckCircle2 className="size-3 text-[#34C759] flex-shrink-0 mt-0.5" />
                <span className="truncate">{s}</span>
              </li>
            ))}
            {(result.failed || []).map((f) => (
              <li key={f.subject} className="flex items-start gap-2" data-testid="email-preview-failed-row">
                <XCircle className="size-3 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                <span className="truncate">
                  {f.subject} <span className="text-[#FF3B30]">— {f.error}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


export default function NotificationConsole() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriberOptions, setSubscriberOptions] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, subsRes] = await Promise.all([
        api.get("/notifications/console"),
        api.get("/notifications/console/subscriber-options").catch(() => null),
      ]);
      setData(cfgRes.data);
      if (subsRes) setSubscriberOptions(subsRes.data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchConsole = async (patch) => {
    try {
      const r = await api.put("/notifications/console", patch);
      setData((d) => ({ ...d, config: r.data }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const createRoster = async (payload) => {
    try {
      await api.post("/notifications/console/rosters", payload);
      toast.success("Roster created");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const updateRoster = async (id, payload) => {
    try {
      await api.put(`/notifications/console/rosters/${id}`, payload);
      toast.success("Roster updated");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const deleteRoster = async (id) => {
    if (!confirm("Delete this roster? Streams subscribed to it will unsubscribe automatically.")) return;
    try {
      await api.delete(`/notifications/console/rosters/${id}`);
      toast.success("Roster deleted");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const saveStream = async (key, patch) => {
    try {
      await api.put(`/notifications/console/streams/${key}`, patch);
      toast.success(`${key} updated`);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (loading || !data) {
    return (
      <div className="p-8 text-zinc-500" data-testid="notif-console-loading">
        Loading notification console…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl space-y-6" data-testid="notification-console-page">
      <div>
        <div className="flex items-center gap-3">
          <BellRing className="size-6 text-[#FF3B30]" />
          <h1 className="text-2xl font-black tracking-tight text-zinc-100">Notification Console</h1>
        </div>
        <div className="text-xs text-zinc-500 mt-1">
          Central control for every automated email sent on behalf of this agency. Transactional
          emails (auth, invites) are not controlled from here — they always send.
        </div>
      </div>

      <MasterSwitchCard cfg={data.config} onChange={patchConsole} />
      <EmailTemplateGalleryCard />
      <QuietHoursCard cfg={data.config} onChange={patchConsole} />
      <RostersCard
        rosters={data.config.rosters || []}
        onCreate={createRoster}
        onUpdate={updateRoster}
        onDelete={deleteRoster}
      />
      <StreamsCard
        streams={data.streams}
        config={data.config}
        rosters={data.config.rosters}
        subscriberOptions={subscriberOptions}
        onSaveStream={saveStream}
      />
      <div className="flex items-center gap-2 text-[11px] text-zinc-600 pt-4">
        <Clock className="size-3" />
        Changes save instantly. See <a href="/notifications-log" className="text-zinc-400 hover:text-zinc-200 underline">Notifications Log</a> for delivery history.
      </div>
    </div>
  );
}
